/**
 * Auth Store - Zustand state for authentication
 *
 * Token storage policy (audit #7 hardening):
 *   - The JWT is NEVER persisted to localStorage. An XSS attacker who can
 *     read localStorage cannot grab the token; they only get the `user`
 *     metadata (name/email/picture), which isn't a credential.
 *   - The token still rides on the in-memory Zustand state during the
 *     active session so existing components that send Authorization: Bearer
 *     keep working without a sweep through every fetch site.
 *   - The authoritative copy is the HttpOnly + Secure session_token cookie
 *     the backend sets on every login/register/verify/OAuth response. The
 *     cookie auto-rides same-origin fetches (and explicit credentials:
 *     'include' fetches), so after page reload — when the in-memory token
 *     is gone — auth still works via the cookie. Backend's get_current_user
 *     prefers the cookie over the Authorization header.
 */

import { create } from 'zustand';
import type { AuthStoreState, AuthUser } from '../types/stores';
import type { Apartment } from '../types/apartment';
import { useUserProfileStore } from './userProfileStore';
import { safeLocal, safeSession } from '@/utils/safeStorage';
// One HTTP client (api/client.ts): base URL, identity headers, timeout.
// This store used to keep a private API_BASE and 17 raw fetches — the
// largest direct fetcher in the app, and the reason api/client.ts had to
// lazy-require this module to read the token.
import { request as apiRequest } from '../api/client';

function getHeaders(token: string | null): Record<string, string> {
  const h: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) h['Authorization'] = `Bearer ${token}`;
  return h;
}

function normalizeAuthError(err: unknown, fallback: string): string {
  const msg = err instanceof Error ? err.message : fallback;
  const lowered = msg.toLowerCase();

  if (
    lowered.includes('abort') ||
    lowered.includes('timed out') ||
    lowered.includes('body stream') ||
    lowered.includes('failed to fetch')
  ) {
    return 'The request was interrupted. Please try signing in again.';
  }

  if (
    lowered.includes('unexpected token') ||
    lowered.includes('not valid json') ||
    lowered.includes('upstream error')
  ) {
    return 'The sign-in service had a temporary error. Please try again.';
  }

  return msg || fallback;
}

async function readResponseBodySafely(res: Response): Promise<any> {
  const text = await res.text();
  if (!text) return null;

  try {
    return JSON.parse(text);
  } catch {
    return { rawText: text };
  }
}

/**
 * After login/register/OAuth: pull saved apartments from the account and
 * merge them into userProfileStore so the AI immediately knows what the
 * user has saved and what notes they've written.
 */
async function syncProfileAfterAuth(token: string) {
  // Phase 4 — merge anonymous localStorage saves into the account first
  // (so the GET below returns the merged set).
  try {
    const { mergeLocalSavesToAccount } = await import('../components/saved/savedListUtils');
    await mergeLocalSavesToAccount(token);
  } catch (e) {
    console.warn('mergeLocalSavesToAccount failed (non-blocking)', e);
  }

  try {
    const res = await apiRequest(`/api/saved-apartments`, {
      headers: getHeaders(token),
      credentials: 'include',
    });
    if (!res.ok) return;
    const data = await res.json();
    const apartments: Array<{ apartment_data?: { name?: string }; user_notes?: string }> =
      data?.apartments || [];

    // Build saves list (name only) and notes map (name -> text) from the
    // account. Merge any localStorage notes too so they're not lost.
    const saves = apartments.map(a => ({ name: a?.apartment_data?.name || '' }));
    const notes: Record<string, string> = {};
    for (const a of apartments) {
      const n = a?.apartment_data?.name;
      if (n && a.user_notes) notes[n] = a.user_notes;
    }
    try {
      const localNotes = JSON.parse(safeLocal.get('tacostreet_local_notes') || '{}');
      for (const [k, v] of Object.entries(localNotes)) {
        if (!notes[k] && typeof v === 'string' && v.trim()) notes[k] = v;
      }
    } catch (e) { console.warn('localStorage notes read failed', e); }

    useUserProfileStore.getState().syncFromAccount(saves, notes);
  } catch (e) {
    console.warn('profile sync after auth failed (non-blocking)', e);
  }
}

// Read storage defensively — private/incognito modes can throw on access.
// Note: we read `user` from localStorage purely as a UX shortcut to avoid
// a flash of unauthenticated UI on page reload. The HttpOnly cookie is
// what actually authenticates; if the cookie is missing or expired,
// checkAuth() will clear the optimistic user state on the first /auth/me
// roundtrip. The JWT itself is NEVER persisted (see audit #7 comment above).
function readUser(): AuthUser | null {
  try {
    const raw = safeLocal.get('user');
    return raw ? (JSON.parse(raw) as AuthUser) : null;
  } catch { return null; }
}

// Audit #7 migration cleanup — older app builds persisted the JWT to
// localStorage under `auth_token`. After deploy, existing users still
// have that key lying around with their last JWT. Wipe it once on init
// so the residual XSS exposure goes away without requiring users to
// log out and back in.
try {
  safeLocal.remove('auth_token');
} catch { /* private mode — nothing to clean */ }

export const useAuthStore = create<AuthStoreState>((set, get) => ({
  token: null,
  user: readUser(),
  isLoading: false,
  error: null,
  authChecked: false,

  // Check authentication on init via the HttpOnly cookie. credentials:
  // 'include' tells the browser to send the cookie even on cross-origin
  // fetches; for same-origin (the prod + dev-proxy case) it's redundant
  // but harmless. No token argument needed. Always flips authChecked so
  // route guards stop waiting and decide based on `user`.
  checkAuth: async () => {
    try {
      const res = await apiRequest(`/api/auth/me`, {
        credentials: 'include',
      });
      if (res.ok) {
        const data = await res.json();
        set({ user: data.user || data, authChecked: true });
        return true;
      }
      get().clearAuth();
      set({ authChecked: true });
      return false;
    } catch {
      // Network error — can't confirm. Mark checked so guards don't hang;
      // any optimistic `user` from localStorage is left intact.
      set({ authChecked: true });
      return false;
    }
  },

  login: async (email: string, password: string, keepSignedIn: boolean = false) => {
    set({ isLoading: true, error: null });
    try {
      const res = await apiRequest(`/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password, keep_signed_in: keepSignedIn }),
      });
      if (!res.ok) {
        // Account UX Phase 2.4 — preserve backend messages that do NOT leak
        // whether the email exists (lockout, rate limit, bot check). For plain
        // 401s we still swallow the specific reason to avoid enumeration.
        const body = await readResponseBodySafely(res);
        const detail =
          typeof body?.detail === 'string'
            ? body.detail
            : typeof body?.rawText === 'string'
              ? body.rawText
              : '';
        const safeMessages = [
          'Account temporarily locked',
          'Too many requests',
          'Too many attempts',
          'Bot verification failed',
        ];
        const isSafeMessage = safeMessages.some(msg => detail.includes(msg));
        if (res.status === 429 || isSafeMessage) {
          throw new Error(detail || 'Too many attempts. Please try again later.');
        }
        if (res.status === 401) {
          throw new Error('Invalid email or password');
        }
        if (res.status === 400) {
          throw new Error('Please check your email and password');
        }
        throw new Error('Login failed. Please try again.');
      }
      const data = await readResponseBodySafely(res);
      if (!data || typeof data !== 'object') {
        throw new Error('Login failed. Please try again.');
      }

      // 2FA challenge — caller must handle the temp_token step
      if (data.requires_2fa) {
        set({ isLoading: false });
        return { requires_2fa: true, temp_token: data.temp_token };
      }

      // Token lives in the HttpOnly cookie the backend set on this response.
      // We keep it in Zustand state in-memory for the active session so
      // existing Bearer-header call sites keep working; never persisted.
      safeLocal.set('user', JSON.stringify(data.user));
      set({ token: data.token, user: data.user, isLoading: false, authChecked: true });

      // Phase 1 — Context System: pull saved apartments into the profile store
      syncProfileAfterAuth(data.token);

      // Admin Visibility Phase 1.3 — link this device's anonymous events.
      try {
        const sessionId = (window as { __ts_session_id?: string }).__ts_session_id;
        if (sessionId) {
          apiRequest(`/api/auth/link-session`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${data.token}` },
            body: JSON.stringify({ session_id: sessionId }),
          }).catch((e) => { console.warn('link-session failed (non-blocking)', e); });
        }
      } catch (e) { console.warn('link-session prep failed', e); }

      return true;
    } catch (err: unknown) {
      const userMsg = normalizeAuthError(err, 'Login failed');
      set({ error: userMsg, isLoading: false });
      return false;
    }
  },

  verify2fa: async (temp_token: string, code: string) => {
    set({ isLoading: true, error: null });
    try {
      const res = await apiRequest(`/api/auth/2fa/verify-login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ temp_token, code }),
        credentials: 'include',
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Invalid code');
      }
      const data = await res.json();
      // Token lives in the HttpOnly cookie the backend set on this response.
      // We keep it in Zustand state in-memory for the active session so
      // existing Bearer-header call sites keep working; never persisted.
      safeLocal.set('user', JSON.stringify(data.user));
      set({ token: data.token, user: data.user, isLoading: false, authChecked: true });
      syncProfileAfterAuth(data.token);
      return true;
    } catch (err: unknown) {
      const msg = normalizeAuthError(err, '2FA verification failed');
      set({ error: msg, isLoading: false });
      return false;
    }
  },

  register: async (name: string, email: string, password: string, turnstileToken?: string, preferredCity?: string) => {
    set({ isLoading: true, error: null });
    try {
      // Admin Visibility Phase 1.2 — pull captured attribution + current session.
      let signupSource: Record<string, unknown> | undefined;
      try {
        const { buildSignupSource } = await import('../utils/signupAttribution');
        const sessionId = (window as any).__ts_session_id || null;
        signupSource = buildSignupSource(preferredCity || null, sessionId);
      } catch { signupSource = undefined; }

      const res = await apiRequest(`/api/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name,
          email,
          password,
          turnstile_token: turnstileToken || null,
          preferred_city: preferredCity || null,
          signup_source: signupSource || null,
        }),
      });
      if (!res.ok) {
        const errorData = await res.json().catch(() => ({ detail: 'Registration failed' }));
        throw new Error(errorData.detail || 'Registration failed');
      }
      // Anti-enumeration register flow: backend no longer returns a JWT
      // here. Activation happens when the user clicks the verify-email
      // link (which lands on /verify-email and issues the JWT there).
      // Caller renders a "check your email" state on this success.
      await res.json();
      set({ isLoading: false });
      return true;
    } catch (err: unknown) {
      const msg = normalizeAuthError(err, 'Registration failed');
      set({ error: msg, isLoading: false });
      return false;
    }
  },

  // Google OAuth — direct redirect to Google's consent screen.
  // Fetches the public client_id from /auth-config at click-time, mints a
  // CSRF state token (random, stored in sessionStorage), and navigates to
  // accounts.google.com. Google sends the user back to /oauth-callback
  // with `code` + `state`, which OAuthCallbackPage validates + exchanges
  // via handleGoogleCallback below.
  startGoogleAuth: async () => {
    let clientId: string | null = null;
    try {
      const cfg = await apiRequest(`/api/config/auth-config`).then(r => r.ok ? r.json() : null);
      clientId = cfg?.google?.client_id || null;
    } catch { /* fall through */ }
    if (!clientId) {
      set({ error: 'Google sign-in is not configured. Please use email + password.' });
      return;
    }
    const state = (typeof crypto !== 'undefined' && crypto.randomUUID)
      ? crypto.randomUUID()
      : Math.random().toString(36).slice(2) + Date.now().toString(36);
    safeSession.set('ts_oauth_state', state);

    const redirectUri = window.location.origin + '/oauth-callback';
    const params = new URLSearchParams({
      client_id: clientId,
      redirect_uri: redirectUri,
      response_type: 'code',
      scope: 'openid email profile',
      state,
      access_type: 'online',
      prompt: 'select_account',
      include_granted_scopes: 'true',
    });
    window.location.href = `https://accounts.google.com/o/oauth2/v2/auth?${params.toString()}`;
  },

  // Handle Google OAuth callback — exchange the one-time code for our JWT.
  // OAuthCallbackPage already validated `state` against sessionStorage
  // before calling this. The redirectUri MUST be byte-identical to the one
  // used in startGoogleAuth (Google enforces this in the token exchange).
  handleGoogleCallback: async (code: string, redirectUri: string) => {
    set({ isLoading: true, error: null });
    try {
      // Admin Visibility Phase 1.2 — pull captured signup attribution +
      // current session, mirror what /auth/register already does.
      let signupSource: Record<string, unknown> | undefined;
      try {
        const { buildSignupSource } = await import('../utils/signupAttribution');
        const sessionId = (window as any).__ts_session_id || null;
        signupSource = buildSignupSource(null, sessionId);
      } catch { signupSource = undefined; }

      const res = await apiRequest(`/api/auth/google/callback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          code,
          redirect_uri: redirectUri,
          signup_source: signupSource || null,
        }),
      });
      const data = await readResponseBodySafely(res);
      if (!res.ok) {
        const detail =
          typeof data?.detail === 'string'
            ? data.detail
            : typeof data?.rawText === 'string'
              ? data.rawText
              : '';
        throw new Error(detail || 'Google sign-in failed');
      }
      if (!data || typeof data !== 'object') {
        throw new Error('Google sign-in failed');
      }

      // Token lives in the HttpOnly cookie the backend set on this response.
      // We keep it in Zustand state in-memory for the active session so
      // existing Bearer-header call sites keep working; never persisted.
      safeLocal.set('user', JSON.stringify(data.user));
      set({ token: data.token, user: data.user, isLoading: false, authChecked: true });
      syncProfileAfterAuth(data.token);
      return true;
    } catch (err: unknown) {
      const msg = normalizeAuthError(err, 'Google sign-in failed');
      set({ error: msg, isLoading: false });
      return false;
    }
  },

  logout: async () => {
    // Call the backend so it can delete the session_token cookie.
    // Without this, the HttpOnly cookie persists in the browser even
    // after local state is wiped, leaving the user effectively still
    // authenticated on their next page load. Fire-and-forget — local
    // state still clears even if the request fails (offline, etc.).
    try {
      await apiRequest(`/api/auth/logout`, {
        method: 'POST',
        credentials: 'include',
      });
    } catch (e) {
      console.warn('logout request failed (non-blocking)', e);
    }
    safeLocal.remove('user');
    set({ token: null, user: null, authChecked: true });
  },

  clearError: () => set({ error: null }),
  clearAuth: () => {
    // Cookie removal happens server-side via POST /auth/logout (see
    // logout below) or naturally on token_version bump. Here we just
    // wipe local UX state.
    safeLocal.remove('user');
    set({ token: null, user: null, authChecked: true });
  },

  // Fetch dashboard data
  fetchDashboard: async () => {
    // Don't bail on a null in-memory token — after a reload it's gone (the JWT
    // is an HttpOnly cookie), but the same-origin request still sends the
    // session cookie, which authenticates server-side.
    const token = get().token;
    try {
      const res = await apiRequest(`/api/dashboard`, { headers: getHeaders(token) });
      if (!res.ok) { if (res.status === 401) get().clearAuth(); return null; }
      return await res.json();
    } catch { return null; }
  },

  // Profile update
  updateProfile: async (profileData: Record<string, unknown>) => {
    const token = get().token;
    try {
      const res = await apiRequest(`/api/auth/profile`, {
        method: 'PUT',
        headers: getHeaders(token),
        body: JSON.stringify(profileData),
      });
      if (!res.ok) throw new Error('Failed to update');
      await res.json();
      const updatedUser = { ...get().user, ...profileData } as AuthUser;
      safeLocal.set('user', JSON.stringify(updatedUser));
      set({ user: updatedUser });
      return true;
    } catch { return false; }
  },

  // Saved apartments
  fetchSavedApartments: async () => {
    // Cookie auth — don't bail on the null in-memory token after reload.
    const token = get().token;
    try {
      const res = await apiRequest(`/api/saved-apartments`, { headers: getHeaders(token) });
      if (!res.ok) return [];
      const data = await res.json();
      return (data.apartments || data || []) as Apartment[];
    } catch { return []; }
  },

  saveApartment: async (aptName: string, aptData: Partial<Apartment>) => {
    const token = get().token;
    try {
      const res = await apiRequest(`/api/saved-apartments`, {
        method: 'POST',
        headers: getHeaders(token),
        credentials: 'include',
        body: JSON.stringify({ apartment_name: aptName, apartment_data: aptData }),
      });
      return res.ok;
    } catch { return false; }
  },

  deleteSavedApartment: async (id: string) => {
    const token = get().token;
    try {
      const res = await apiRequest(`/api/saved-apartments/${id}`, {
        method: 'DELETE',
        headers: getHeaders(token),
        credentials: 'include',
      });
      return res.ok;
    } catch { return false; }
  },

  // Saved searches
  fetchSavedSearches: async () => {
    // Cookie auth — don't bail on the null in-memory token after reload.
    const token = get().token;
    try {
      const res = await apiRequest(`/api/saved-searches`, { headers: getHeaders(token) });
      if (!res.ok) return [];
      const data = await res.json();
      return data.searches || data || [];
    } catch { return []; }
  },

  deleteSavedSearch: async (id: string) => {
    const token = get().token;
    try {
      const res = await apiRequest(`/api/saved-searches/${id}`, {
        method: 'DELETE',
        headers: getHeaders(token),
      });
      return res.ok;
    } catch { return false; }
  },

  // Search all apartments (for autocomplete)
  searchApartments: async (query: string) => {
    try {
      const res = await apiRequest(`/api/search_v2/autocomplete?q=${encodeURIComponent(query)}&limit=10`);
      if (!res.ok) return [];
      const data = await res.json();
      return data.results || data || [];
    } catch { return []; }
  },
}));
